import type { Map as ImMap } from 'immutable';
import * as React from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { FieldAttributes } from 'formik';
import { Field } from 'formik';

import LockOutlined from '@mui/icons-material/LockOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import type { Business } from 'app-components/flux/business/businessTypes';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import FormikTextField from 'components/Formik/TextField';
import UsageAndEntityIcon from 'components/UsageAndEntityIcon';
import { makeStyles } from 'tss-react/mui';

type GetMenuItemsProps = {
  onClick?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>, id: 'PERSONAL' | string) => void;
  showPersonal?: boolean;
};

type UsageAndEntitySelectorProps = {
  businessesById: ImMap<string, Business>;
  name: string;
  readonly?: boolean;
  hidePersonal?: boolean;
} & FieldAttributes<any>;

const useStyles = makeStyles()((theme) => ({
  selectReadOnly: {
    display: 'flex',
    borderRadius: '12px',
    color: 'inherit',
    backgroundColor: theme.palette.background.paper,
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.paper,
      '& .Mui-disabled': {
        WebkitTextFillColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
      },
    },
    '& .Mui-disabled': {
      opacity: 1,
      backgroundColor: theme.palette.background.paper,
      color: 'inherit',
    },
  },
}));

export const getMenuItems = (
  businessesById: ImMap<string, Business>,
  { onClick, showPersonal = true }: GetMenuItemsProps = {},
) => {
  const businesses =
    businessesById?.isEmpty() ?? true
      ? null
      : businessesById
          .valueSeq()
          .filter((business) => !business.isClosed)
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' }))
          .toList();

  const values = [];
  if (showPersonal) {
    values.push(
      <MenuItem
        divider={!!businesses}
        key='PERSONAL'
        value='PERSONAL'
        onClick={(event) => onClick?.(event, 'PERSONAL')}
      >
        <ListItemIcon>
          <UsageAndEntityIcon usageIcon='PERSONAL' size='small' />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ component: 'span' }}>Personal</ListItemText>
      </MenuItem>,
    );
  }
  if (businesses) {
    if (showPersonal) {
      values.push(<ListSubheader key='sh_business'>Business</ListSubheader>);
    }
    businesses.forEach((business) => {
      const id = getId(business);
      values.push(
        <MenuItem key={id} value={id} onClick={(event) => onClick?.(event, id)}>
          <ListItemIcon>
            <UsageAndEntityIcon usageIcon={business?.taxGroup || 'BUSINESS'} color={business.color} size='small' />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ component: 'span' }}>{business.name}</ListItemText>
        </MenuItem>,
      );
    });
  }
  return values;
};

function UsageAndEntitySelector(props: UsageAndEntitySelectorProps) {
  const { businessesById, name, disabled, readOnly, hidePersonal, ...other } = props;
  const { classes } = useStyles();
  const theme = useTheme();

  const renderValue = (value: string) => {
    const business = businessesById?.get(value);
    const usageType = business || value === 'BUSINESS' ? 'BUSINESS' : 'PERSONAL';
    const usageName = business?.name || (usageType === 'BUSINESS' ? 'Business' : 'Personal');
    return (
      <Stack direction='row' alignSelf='center'>
        <UsageAndEntityIcon usageIcon={business?.taxGroup || value} size='xsmall' color={business?.color} />
        <Typography
          variant='paragraphRegular'
          sx={{
            display: 'block',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            width: '120px',
            marginLeft: theme.spacing(1),
          }}
        >
          {usageName}
        </Typography>
      </Stack>
    );
  };

  return (
    <Field
      component={FormikTextField}
      name={name}
      select
      size='small'
      variant='outlined'
      SelectProps={{ renderValue }}
      disabled={disabled || readOnly}
      readOnly={readOnly}
      IconComponent={readOnly ? LockOutlined : undefined}
      classes={{
        root: readOnly ? classes.selectReadOnly : undefined,
      }}
      {...other}
    >
      {disabled || readOnly ? null : getMenuItems(businessesById, { showPersonal: !hidePersonal })}
    </Field>
  );
}

export default UsageAndEntitySelector;
